import type { KYCQuestionDefinition } from '@anchorage/graphql/generated';
import { KYCQuestionRuleType } from '@anchorage/graphql/generated';
import { FormDatepicker, reactHookForm, useWatch } from '@latitude/form';

type Props = {
  question: KYCQuestionDefinition;
  isDisabled?: boolean;
};

function DateComponent({ question, isDisabled = false }: Props) {
  const { control, setValue } = reactHookForm.useFormContext();

  // Extract min/max dates from rules for the datepicker UI constraints
  // These are separate from validation and control what dates are selectable
  const maxDateRule = question.rules?.find(
    (rule) => rule.type === KYCQuestionRuleType.MAX_DATE,
  );
  const minDateRule = question.rules?.find(
    (rule) => rule.type === KYCQuestionRuleType.MIN_DATE,
  );

  // Watch referenced fields to make min/max dates reactive
  const maxDateFieldName = maxDateRule?.questionId
    ? `${maxDateRule.questionId}.freeformText`
    : null;
  const minDateFieldName = minDateRule?.questionId
    ? `${minDateRule.questionId}.freeformText`
    : null;

  // Watch max date field if it exists
  const maxDateWatchedValue = useWatch({
    control,
    name: maxDateFieldName || '',
    disabled: !maxDateFieldName,
  }) as string | undefined;

  // Watch min date field if it exists
  const minDateWatchedValue = useWatch({
    control,
    name: minDateFieldName || '',
    disabled: !minDateFieldName,
  }) as string | undefined;

  const maxDateReferencedValue = maxDateFieldName
    ? maxDateWatchedValue
    : undefined;
  const minDateReferencedValue = minDateFieldName
    ? minDateWatchedValue
    : undefined;

  // Helper to resolve date value, supporting questionId (cross-field) and static values
  const resolveDateValue = (
    ruleValue: string,
    questionId?: string,
    watchedValue?: string,
  ): Date | undefined => {
    let dateValue = ruleValue;

    // If questionId is provided, get the date from the watched value
    if (questionId && watchedValue) {
      dateValue = watchedValue;
    }

    if (!dateValue) {
      return undefined;
    }

    return dateValue === 'TODAY' ? new Date() : new Date(dateValue);
  };

  const maxDate = maxDateRule?.value
    ? resolveDateValue(
        maxDateRule.value,
        maxDateRule.questionId || '',
        maxDateReferencedValue,
      )
    : undefined;
  const minDate = minDateRule?.value
    ? resolveDateValue(
        minDateRule.value,
        minDateRule.questionId || '',
        minDateReferencedValue,
      )
    : undefined;

  return (
    <FormDatepicker
      mode="single"
      control={control}
      maxDate={maxDate}
      minDate={minDate}
      disabled={isDisabled}
      name={`${question.id}.freeformText`}
      placeholder="MM/DD/YYYY"
      // FormDatepicker already sets the date to null on clear.
      onClear={() => setValue(`${question.id}.freeformOptionID`, [])}
    />
  );
}

export { DateComponent };
